import type { Movie } from "@/types/movie";
import { movies, genres } from "@/mocks/movies";

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getAllMovies(): Movie[] {
  return movies;
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getTopRatedMovies(limit = 4): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getMoviesByGenre(genre: string): Movie[] {
  return movies.filter((movie) => movie.genres.includes(genre));
}

export function getFeaturedMovies(limit = 5): Movie[] {
  const featuredIds = [
    "xinghai-guhang",
    "anye-zhuixiong",
    "shanhai-qiyuan",
    "wusheng-zhengci",
    "yingxiong-guilai",
  ];
  const featured = featuredIds
    .map((id) => movies.find((movie) => movie.id === id))
    .filter((movie): movie is Movie => Boolean(movie));
  return featured.slice(0, limit);
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.director,
      movie.region,
      movie.language,
      movie.tagline,
      movie.synopsis,
      ...movie.genres,
      ...movie.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export interface MovieFilters {
  subGenre?: string;
  region?: string;
  year?: number | "";
  sortBy?: "time" | "rating" | "heat";
}

export function applyFilters(list: Movie[], filters: MovieFilters): Movie[] {
  const { subGenre, region, year, sortBy } = filters;
  let result = [...list];

  if (subGenre) {
    result = result.filter((movie) => movie.genres.includes(subGenre));
  }
  if (region) {
    result = result.filter((movie) => movie.region === region);
  }
  if (year) {
    result = result.filter((movie) => movie.year === year);
  }

  switch (sortBy) {
    case "rating":
      result.sort((a, b) => b.rating - a.rating);
      break;
    case "heat":
      result.sort((a, b) => b.heat - a.heat);
      break;
    default:
      result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
      break;
  }
  return result;
}

export function getSubGenres(genre: string): string[] {
  const set = new Set<string>();
  getMoviesByGenre(genre).forEach((movie) => {
    movie.genres.forEach((g) => {
      if (g !== genre) set.add(g);
    });
  });
  return Array.from(set);
}

export function getRegionsInGenre(genre: string): string[] {
  const set = new Set<string>();
  getMoviesByGenre(genre).forEach((movie) => set.add(movie.region));
  return Array.from(set);
}

export function getYearsInGenre(genre: string): number[] {
  const set = new Set<number>();
  getMoviesByGenre(genre).forEach((movie) => set.add(movie.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function getRecommendations(movie: Movie, limit = 6): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.director === movie.director) score += 5;
      item.genres.forEach((g) => {
        if (movie.genres.includes(g)) score += 2;
      });
      if (item.region === movie.region) score += 1;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  const items = list.slice(start, start + pageSize);
  return { items, total, totalPages, current, pageSize };
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}小时${m}分`;
  return `${m}分钟`;
}

export function getGenreInfo(slug: string) {
  return genres.find((genre) => genre.slug === slug);
}